if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
      this.itemSelector = this.dataset.itemSelector || '.slideshow__slide';
      this.ignoreResizeEvent = true;
    }

    init() {
      super.init();
      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      this.enableSliderLooping = true;
      this.autoplayButtonQuerySelector = '.slideshow__autoplay';
      this.autoPlayPausedClass = 'slideshow__autoplay--paused';
  
      if (!this.sliderControlWrapper) return;
  
      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
  
      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
      this.setSlideVisibility();

      this.querySelectorAll('.banner__price').forEach(ele => {
        ele.innerHTML = Shopify.showPrice(ele.dataset.price * 1);
      });
    }
  
    update() {
      super.update();

      // Update active slide classes
      const oldActive = this.querySelector(`${this.itemSelector}.active`);
      if (oldActive) oldActive.classList.remove('active');
    
      const activeSlide = this.getActiveSlide();
      if (activeSlide) activeSlide.classList.add('active');
    
      // --- Animate autoplay button (visual sync) ---
      if (this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.remove(this.autoPlayPausedClass);
        this.sliderAutoplayButton
          .querySelector('.slideshow__progress__path')
          .getAnimations()
          .forEach(anim => {
            anim.cancel();
            anim.play();
          });
      }
    }

    getActiveSlide() {
      // Defensive guard: ensure currentPage is valid
      if (!this.currentPage || isNaN(this.currentPage)) {
        // Try to reset pages if not initialized yet
        if (typeof this.initPages === 'function') {
          this.initPages();
        }
    
        // Fallback to first visible slide if still invalid
        this.currentPage = this.currentPage && !isNaN(this.currentPage)
          ? this.currentPage
          : 1;
      }
    
      // Ensure itemSelector is valid
      const selector = `${this.itemSelector}:nth-child(${this.currentPage})`;
    
      try {
        return this.querySelector(selector);
      } catch (err) {
        console.warn('⚠️ [SlideshowComponent] Invalid selector:', selector, err);
        // fallback gracefully
        return this.querySelector(this.itemSelector);
      }
    }
  
    setSlideVisibility() {
      const activeElement = document.activeElement;
      const activeIndex = this.currentPage ? this.currentPage - 1 : 0;

      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll('a');
        const isActive = index === activeIndex;

        // If a focused element is inside a slide that is about to be hidden,
        // move focus somewhere safe before toggling aria-hidden.
        if (!isActive && activeElement && item.contains(activeElement)) {
          const activeSlide = this.sliderItemsToShow[activeIndex];

          // Prefer a focusable element inside the active slide
          let focusTarget =
            activeSlide &&
            activeSlide.querySelector(
              'a[href], button, [role="button"], [tabindex]:not([tabindex="-1"])'
            );

          // Fallback: use an active dot or navigation button
          if (!focusTarget) {
            focusTarget =
              this.querySelector('.slider-counter__link[aria-current="true"]') ||
              this.querySelector('.slider-button--next, .slider-button--prev') ||
              this; // as a last resort, focus slider container
          }

          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus();
          } else if (typeof activeElement.blur === 'function') {
            activeElement.blur();
          }
        }

        if (isActive) {
          if (linkElements.length) {
            linkElements.forEach((button) => {
              button.removeAttribute('tabindex');
            });
          }
          item.setAttribute('aria-hidden', 'false');
          item.removeAttribute('tabindex');
        } else {
          if (linkElements.length) {
            linkElements.forEach((button) => {
              button.setAttribute('tabindex', '-1');
            });
          }
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
        }
      });
    }
  
    play() {
      super.play();
      this.playVideo();
      this.runAutoplayButton();
    }

    playVideo() {
      if (this.handleBackgroundVideo(this.getActiveSlide())) return;

      // --- Cleanup old video event listeners ---
      if (this._videoListeners?.length) {
        this._videoListeners.forEach(({ el, type, handler }) => {
          el.removeEventListener(type, handler);
        });
      }
      this._videoListeners = [];

      this.querySelectorAll('deferred-media').forEach(dm => {
        // Pause all other DeferredMedia when slide changes
        if (window.customElements?.get('deferred-media')) {
          try {
            customElements.upgrade(dm);
            dm.pause();
          } catch (e) {
            // Ignore DOM upgrade race conditions
          }
        }
      });
    
      if (this.prevButton) this.prevButton.removeAttribute('disabled');
    
      const activeSlide = this.getActiveSlide();
      // --- Handle DeferredMedia playback ---
      const dm = activeSlide ? activeSlide.querySelector('deferred-media') : null;
    
      // Helper: update CSS --speed for autoplay button
      const setAutoplaySpeedCSS = (seconds) => {
        if (this.sliderAutoplayButton) {
          this.sliderAutoplayButton.style.setProperty('--speed', `${seconds}s`);
        }
      };
    
      if (!dm) {
        
        // No video → use default autoplay delay
        setAutoplaySpeedCSS(this.autoplaySpeed / 1000);
    
        if (this.isAutoplay && this.autoplayButtonIsSetToPlay) {
          clearTimeout(this.autoplay);
          this.autoplay = setTimeout(() => this.autoRotateSlides(), this.autoplaySpeed);
        }
        return;
      }
    
      // --- Video slide handling ---
      dm.play().then(() => {
        const getVideoEl = () => dm.deferredElement || dm.querySelector('video');
    
        const startWithDuration = () => {
          const videoEl = getVideoEl();
          if (!videoEl) return;
      
          const duration = (videoEl.duration && !isNaN(videoEl.duration)) ? videoEl.duration : 5;
          setAutoplaySpeedCSS(duration); // 🔥 update CSS --speed
      
          // Autoplay slideshow delay based on video duration
          if (this.isAutoplay && this.autoplayButtonIsSetToPlay) {
            clearTimeout(this.autoplay);
            this.autoplay = setTimeout(() => this.autoRotateSlides(), duration * 1000);
      
            // Handle early video end
            const onEnded = () => {
              clearTimeout(this.autoplay);
              this.autoRotateSlides();
            };
            videoEl.addEventListener('ended', onEnded, { once: true });
            this._videoListeners.push({ el: videoEl, type: 'ended', handler: onEnded });
          }
        };
      
        const waitForVideo = () => {
          const videoEl = getVideoEl();
          if (!videoEl) {
            requestAnimationFrame(waitForVideo);
            return;
          }
          if (isNaN(videoEl.duration) || !videoEl.duration) {
            const onLoaded = startWithDuration;
            videoEl.addEventListener('loadedmetadata', onLoaded, { once: true });
            this._videoListeners.push({ el: videoEl, type: 'loadedmetadata', handler: onLoaded });
          } else {
            startWithDuration();
          }
        };
      
        waitForVideo();
      });
    }

    /**
     * Slides rendered by the `banner-video` snippet use a plain looping <video>,
     * not <deferred-media>, so the duration-based autoplay below never saw them
     * and the slide moved on after the fixed delay. Play the visible video once
     * and advance when it ends.
     * @returns {boolean} true when the active slide had a background video
     */
    handleBackgroundVideo(activeSlide) {
      this.querySelectorAll('video.banner__video').forEach((video) => {
        if (!activeSlide || !activeSlide.contains(video)) video.pause();
      });

      const videoEl = activeSlide && !activeSlide.querySelector('deferred-media')
        ? Array.from(activeSlide.querySelectorAll('video.banner__video')).find((video) => video.getClientRects().length)
        : null;

      if (!videoEl) {
        this._activeBgVideo = null;
        return false;
      }

      const rotate = this.isAutoplay && this.autoplayButtonIsSetToPlay !== false && this.sliderItemsToShow.length > 1;
      if (!rotate) {
        this._activeBgVideo = null;
        videoEl.loop = true;
        videoEl.play().catch(() => {});
        return true;
      }

      // update() calls play() on every scroll and resize event (including the
      // mobile address bar collapsing); keep the running video going.
      if (videoEl === this._activeBgVideo) {
        this.armBackgroundVideoTimer(videoEl);
        return true;
      }

      if (this._videoListeners?.length) {
        this._videoListeners.forEach(({ el, type, handler }) => el.removeEventListener(type, handler));
      }
      this._videoListeners = [];

      this._activeBgVideo = videoEl;
      this._bgVideoBlocked = false;
      videoEl.loop = false;
      videoEl.currentTime = 0;

      const onEnded = () => {
        clearTimeout(this.autoplay);
        this._activeBgVideo = null;
        this.autoRotateSlides();
      };
      videoEl.addEventListener('ended', onEnded, { once: true });
      this._videoListeners.push({ el: videoEl, type: 'ended', handler: onEnded });

      const playing = videoEl.play();
      if (playing) {
        playing.catch(() => {
          // Autoplay refused (e.g. iOS Low Power Mode): rotate on the normal delay.
          if (this._activeBgVideo !== videoEl) return;
          this._bgVideoBlocked = true;
          this.armBackgroundVideoTimer(videoEl);
        });
      }

      this.armBackgroundVideoTimer(videoEl);
      return true;
    }

    armBackgroundVideoTimer(videoEl) {
      clearTimeout(this.autoplay);

      if (this._bgVideoBlocked) {
        this.autoplay = setTimeout(() => this.autoRotateSlides(), this.autoplaySpeed);
        return;
      }

      const duration = videoEl.duration;
      if (!duration || isNaN(duration)) {
        videoEl.addEventListener('loadedmetadata', () => {
          if (this._activeBgVideo === videoEl) this.armBackgroundVideoTimer(videoEl);
        }, { once: true });
        return;
      }

      // 'ended' normally advances the slide; this only covers a stalled stream.
      const remaining = Math.max(duration - videoEl.currentTime, 0);
      this.autoplay = setTimeout(() => this.autoRotateSlides(), (remaining * 1.5 + 2) * 1000);
    }

    pause() {
      super.pause();
      this.runAutoplayButton(false);
      const activeSlide = this.getActiveSlide();
      const dm = activeSlide ? activeSlide.querySelector('deferred-media') : null;
      if(dm) {
        dm.pause();
      }
    }

    runAutoplayButton(play = true) {
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.toggle(this.autoPlayPausedClass, !play);
        this.sliderAutoplayButton.querySelector('.slideshow__progress__path').getAnimations().forEach((anim) => {
          if(play) {
            anim.cancel();
            anim.play();
          } else {
            anim.pause();
          }
        });
      }
    }

    beforeUpdateAutoplay() {
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.querySelector('.slideshow__progress__path').getAnimations().forEach((anim) => {
          anim.pause();
        });
      }
    }
  });
}